/* prettier-ignore-file */
import React, { useEffect, useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Switch,
  Image,
  Platform,
} from "react-native";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp, Timestamp, arrayUnion } from "firebase/firestore";
import { db, auth, storage } from "../firebase.config";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { getCollectionPath } from "../utils/dbPaths";
import { useBookings, useAuthStore } from "../stores";
import { createChangeTrackingEntry } from "../utils/helperFuns";
import AuthHeader from "../components/AuthHeader";
import { getTenantId, getStoreId } from "../services/userStoreCache";
import PinAuthLayer from "../components/PinAuthLayer";
import { isDeviceTrusted } from "../utils/deviceTrust";

const ProblemReports = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [breakdownReports, setBreakdownReports] = useState([]);
  const [updating, setUpdating] = useState({});
  const [isVerified, setIsVerified] = useState(false);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [photoModalUrl, setPhotoModalUrl] = useState(null);
  const [locationStrings, setLocationStrings] = useState({}); // Map of timestamp -> location string
  const [addingPhotoToReport, setAddingPhotoToReport] = useState(null); // Track which report is having a photo added
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const bookings = useBookings((state) => state.bookings);

  // Load booking data
  useEffect(() => {
    const loadBooking = async () => {
      if (!bookingId) {
        setLoading(false);
        return;
      }

      // First check Zustand store
      const foundInStore = bookings.find(b => b.id === bookingId || b.bookingId === bookingId);
      
      if (foundInStore) {
        setBooking(foundInStore);
        setBreakdownReports(foundInStore.breakdownReports || []);
        setLoading(false);
        return;
      }

      // Not in store - check Firestore
      try {
        const bookingsPath = getCollectionPath("bookings");
        
        const bookingDoc = await getDoc(doc(db, bookingsPath, bookingId));
        
        if (bookingDoc.exists()) {
          const bookingData = { id: bookingDoc.id, ...bookingDoc.data() };
          setBooking(bookingData);
          setBreakdownReports(bookingData.breakdownReports || []);
        }
      } catch (err) {
        console.error("❌ Error loading booking:", err);
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [bookingId, bookings]);

  // Check device trust for PIN authentication
  useEffect(() => {
    if (isAuthenticated) {
      setIsVerified(true);
      return;
    }

    if (!bookingId) {
      return;
    }

    // Check if device is trusted
    const trustInfo = isDeviceTrusted(bookingId);
    if (trustInfo) {
      console.log("✅ Device is trusted for ProblemReports");
      setIsVerified(true);
    } else {
      console.log("🔒 Device not trusted, showing verification");
      setIsVerified(false);
    }
  }, [bookingId, isAuthenticated]);

  // Handle toggle completed status
  const handleToggleCompleted = async (reportTimestamp, currentCompleted) => {
    if (!booking || !bookingId) return;

    // Check Firebase Auth - rules require authenticated user
    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      alert("You must be signed in to update reports. Please sign in and try again.");
      return;
    }

    const reportId = `${reportTimestamp?.seconds || reportTimestamp}_${Date.now()}`;
    setUpdating(prev => ({ ...prev, [reportId]: true }));

    // Define bookingsPath outside try block for error logging
    const bookingsPath = getCollectionPath("bookings");

    try {
      const bookingRef = doc(db, bookingsPath, bookingId);
      
      // Get current reports
      const bookingDoc = await getDoc(bookingRef);
      if (!bookingDoc.exists()) {
        throw new Error("Booking not found");
      }

      const currentReports = bookingDoc.data().breakdownReports || [];
      
      // Find report by timestamp (unique identifier)
      const reportIndex = currentReports.findIndex((report) => {
        const reportTime = report.timestamp?.seconds || report.timestamp;
        const targetTime = reportTimestamp?.seconds || reportTimestamp;
        return reportTime === targetTime;
      });

      if (reportIndex === -1) {
        throw new Error(`Report not found with timestamp: ${reportTimestamp}`);
      }

      // Update the specific report - preserve all fields including timestamp
      const updatedReports = currentReports.map((report, idx) => {
        if (idx === reportIndex) {
          // Explicitly construct the updated report to avoid serialization issues
          const updatedReport = {
            timestamp: report.timestamp, // Preserve Firestore Timestamp
            imageUrls: report.imageUrls || [],
            completed: !currentCompleted, // Update this field
            description: report.description || "",
          };

          // Add optional fields if they exist
          if (report.location !== undefined && report.location !== null) updatedReport.location = report.location;
          if (report.latitude !== undefined) updatedReport.latitude = report.latitude;
          if (report.longitude !== undefined) updatedReport.longitude = report.longitude;
          if (report.roadsideBreakdown !== undefined) updatedReport.roadsideBreakdown = report.roadsideBreakdown;

          return updatedReport;
        }
        return report;
      });

      // Get user metadata for change tracking (if authenticated)
      const selectedUser = useAuthStore.getState().selectedUser;
      const userMetadata = selectedUser ? {
        userId: selectedUser.id,
        firstName: selectedUser.firstName || "",
        lastName: selectedUser.lastName || "",
      } : null;

      const updateData = {
        breakdownReports: updatedReports,
        updatedAt: Timestamp.now(),
      };

      // Add change tracking if user metadata is available
      if (userMetadata) {
        const changeEntry = createChangeTrackingEntry(userMetadata, {
          changeType: "problem_report",
          fieldChanged: "breakdownReports",
          description: `Problem report ${reportId ? "updated" : "created"}`,
          timestamp: Timestamp.now(),
        });
        
        if (changeEntry) {
          updateData.editedBy = arrayUnion(changeEntry);
        }
      }

      // Update in Firestore
      await updateDoc(bookingRef, updateData);

      // Update local state
      setBreakdownReports(updatedReports);
      
      // Update booking in store if it exists
      const foundInStore = bookings.find(b => b.id === bookingId || b.bookingId === bookingId);
      if (foundInStore) {
        // The store will be updated on next fetch, but we can optimistically update
        setBooking({ ...booking, breakdownReports: updatedReports });
      }

      console.log(`✅ Report ${reportIndex} marked as ${!currentCompleted ? 'completed' : 'incomplete'}`);
    } catch (error) {
      console.error("❌ Error updating report:", error);
      console.error("❌ Error details:", {
        message: error.message,
        code: error.code,
        stack: error.stack,
        reportIndex,
        currentCompleted,
        bookingId,
        bookingsPath
      });
      alert(`Failed to update report status: ${error.message || 'Please try again.'}`);
    } finally {
      setUpdating(prev => {
        const newState = { ...prev };
        delete newState[reportId];
        return newState;
      });
    }
  };

  // Handle view on map
  const handleViewOnMap = (report) => {
    const mapUrl = `/map-viewer?lat=${report.latitude}&lng=${report.longitude}&bookingId=${bookingId}&imageUrl=${encodeURIComponent(report.imageUrl)}`;
    navigate(mapUrl);
  };

  // Capture photo from camera (same as ViewBooking)
  const capturePhoto = async () => {
    // Check if getUserMedia is available
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error("Camera access not supported by your browser");
    }

    console.log("📸 Requesting camera access...");

    // Request camera access
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: "environment", // Prefer back camera on mobile
        width: { ideal: 1920 },
        height: { ideal: 1080 }
      }
    });

    // Create video element to display camera stream
    const video = document.createElement("video");
    video.srcObject = stream;
    video.autoplay = true;
    video.playsInline = true;
    video.style.position = "fixed";
    video.style.top = "0";
    video.style.left = "0";
    video.style.width = "100%";
    video.style.height = "100%";
    video.style.objectFit = "cover";
    video.style.zIndex = "10000";
    video.style.backgroundColor = "#0F1A19";

    // Create capture button
    const captureButton = document.createElement("button");
    captureButton.textContent = "📸 Capture Photo";
    captureButton.style.position = "fixed";
    captureButton.style.bottom = "40px";
    captureButton.style.left = "50%";
    captureButton.style.transform = "translateX(-50%)";
    captureButton.style.zIndex = "10001";
    captureButton.style.padding = "16px 32px";
    captureButton.style.fontSize = "18px";
    captureButton.style.fontWeight = "bold";
    captureButton.style.backgroundColor = "#C4453D";
    captureButton.style.color = "#FFFFFF";
    captureButton.style.border = "none";
    captureButton.style.borderRadius = "8px";
    captureButton.style.cursor = "pointer";
    captureButton.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";

    // Create cancel button
    const cancelButton = document.createElement("button");
    cancelButton.textContent = "✕ Cancel";
    cancelButton.style.position = "fixed";
    cancelButton.style.top = "20px";
    cancelButton.style.right = "20px";
    cancelButton.style.zIndex = "10001";
    cancelButton.style.padding = "12px 24px";
    cancelButton.style.fontSize = "16px";
    cancelButton.style.fontWeight = "bold";
    cancelButton.style.backgroundColor = "rgba(0,0,0,0.7)";
    cancelButton.style.color = "#FFFFFF";
    cancelButton.style.border = "none";
    cancelButton.style.borderRadius = "8px";
    cancelButton.style.cursor = "pointer";

    document.body.appendChild(video);
    document.body.appendChild(captureButton);
    document.body.appendChild(cancelButton);

    // Wait for video to be ready
    await new Promise((resolve) => {
      video.onloadedmetadata = () => {
        video.play();
        resolve();
      };
    });

    // Capture photo when button is clicked
    const imageBase64 = await new Promise((resolve, reject) => {
      const cleanup = () => {
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(video);
        document.body.removeChild(captureButton);
        document.body.removeChild(cancelButton);
      };

      captureButton.onclick = () => {
        // Create canvas to capture frame
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0);

        // Convert to base64
        const base64 = canvas.toDataURL("image/jpeg", 0.9);
        cleanup();
        resolve(base64);
      };

      cancelButton.onclick = () => {
        cleanup();
        reject(new Error("Camera capture cancelled"));
      };
    });

    console.log("📸 Image captured, size:", imageBase64.length);
    return imageBase64;
  };

  // Add photo to existing report
  const handleAddPhotoToReport = async (reportTimestamp) => {
    if (!booking || !bookingId) return;

    // Check Firebase Auth
    const currentUser = getAuth().currentUser;
    if (!currentUser) {
      alert("You must be signed in to add photos. Please sign in and try again.");
      return;
    }

    setAddingPhotoToReport(reportTimestamp);
    const bookingsPath = getCollectionPath("bookings");

    try {
      // Capture photo
      const imageBase64 = await capturePhoto();

      // Convert base64 to blob
      const response = await fetch(imageBase64);
      const blob = await response.blob();

      // Upload to Firebase Storage
      const timestamp = Date.now();
      const imagePath = `bookings/${bookingId}/media/breakdowns/${timestamp}_add.jpg`;
      const storageRef = ref(storage, imagePath);

      await uploadBytes(storageRef, blob, {
        contentType: "image/jpeg",
      });

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      console.log("✅ Photo uploaded:", downloadURL);

      // Update the report in Firestore
      const bookingRef = doc(db, bookingsPath, bookingId);
      const bookingDoc = await getDoc(bookingRef);

      if (!bookingDoc.exists()) {
        throw new Error("Booking not found");
      }

      const currentReports = bookingDoc.data().breakdownReports || [];

      // Find report by timestamp
      const reportIndex = currentReports.findIndex((report) => {
        const reportTime = report.timestamp?.seconds || report.timestamp;
        const targetTime = reportTimestamp?.seconds || reportTimestamp;
        return reportTime === targetTime;
      });

      if (reportIndex === -1) {
        throw new Error(`Report not found with timestamp: ${reportTimestamp}`);
      }

      // Add new image URL to the report's imageUrls array
      const updatedReports = currentReports.map((report, idx) => {
        if (idx === reportIndex) {
          return {
            ...report,
            imageUrls: [...(report.imageUrls || []), downloadURL],
          };
        }
        return report;
      });

      // Get user metadata for change tracking (if authenticated)
      const selectedUserForPhoto = useAuthStore.getState().selectedUser;
      const userMetadataForPhoto = selectedUserForPhoto ? {
        userId: selectedUserForPhoto.id,
        firstName: selectedUserForPhoto.firstName || "",
        lastName: selectedUserForPhoto.lastName || "",
      } : null;

      const photoUpdateData = {
        breakdownReports: updatedReports,
        updatedAt: Timestamp.now(),
      };

      // Add change tracking if user metadata is available
      if (userMetadataForPhoto) {
        const changeEntry = createChangeTrackingEntry(userMetadataForPhoto, {
          changeType: "problem_report",
          fieldChanged: "breakdownReports",
          description: "Photo added to problem report",
          timestamp: Timestamp.now(),
        });
        
        if (changeEntry) {
          photoUpdateData.editedBy = arrayUnion(changeEntry);
        }
      }

      // Update in Firestore
      await updateDoc(bookingRef, photoUpdateData);

      // Update local state
      setBreakdownReports(updatedReports);

      // Update booking in store if it exists
      const foundInStore = bookings.find(b => b.id === bookingId || b.bookingId === bookingId);
      if (foundInStore) {
        setBooking({ ...booking, breakdownReports: updatedReports });
      }

      console.log(`✅ Photo added to report`);
    } catch (error) {
      console.error("❌ Error adding photo to report:", error);
      if (error.message && !error.message.includes("cancelled")) {
        alert(`Failed to add photo: ${error.message || 'Please try again.'}`);
      }
    } finally {
      setAddingPhotoToReport(null);
    }
  };

  // Reverse geocode coordinates to get city/township with state
  const reverseGeocode = async (latitude, longitude) => {
    try {
      const GOOGLE_MAPS_API_KEY = "AIzaSyDnMrIVkx-6V0Brm4pxezeOVcTOELsTmdQ";
      const url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${GOOGLE_MAPS_API_KEY}`;

      const response = await fetch(url);
      const data = await response.json();

      if (data.status === "OK" && data.results && data.results.length > 0) {
        // Extract city/township and state from address components
        const result = data.results[0];
        const addressComponents = result.address_components || [];

        // Look for city, town, or locality and state
        let city = null;
        let state = null;

        for (const component of addressComponents) {
          // Get city/township
          if (!city && component.types.includes("locality")) {
            city = component.long_name;
          } else if (!city && component.types.includes("administrative_area_level_2")) {
            // County level fallback
            city = component.long_name;
          } else if (!city && component.types.includes("administrative_area_level_3")) {
            // Sub-county level fallback
            city = component.long_name;
          }

          // Get state abbreviation
          if (!state && component.types.includes("administrative_area_level_1")) {
            state = component.short_name; // Use short_name for state abbreviation (e.g., "FL")
          }
        }

        // Fallback to formatted address if no city found
        if (!city && result.formatted_address) {
          // Try to extract city from formatted address
          const parts = result.formatted_address.split(",");
          if (parts.length > 1) {
            city = parts[parts.length - 2].trim(); // Usually city is second to last
          }
        }

        // Format: "City, ST" or just "City" if no state
        if (city && state) {
          return `${city}, ${state}`;
        } else if (city) {
          return city;
        } else if (result.formatted_address) {
          return result.formatted_address;
        }

        return "Unknown location";
      }

      return "Unknown location";
    } catch (error) {
      console.error("❌ Error reverse geocoding:", error);
      return "Unknown location";
    }
  };

  // Load location strings for all reports with GPS coordinates
  useEffect(() => {
    const loadLocationStrings = async () => {
      const locationPromises = breakdownReports
        .filter(report => report.latitude && report.longitude)
        .map(async (report) => {
          const timestamp = report.timestamp?.seconds || report.timestamp;
          if (!timestamp || locationStrings[timestamp]) {
            return; // Already loaded or no timestamp
          }

          const locationString = await reverseGeocode(report.latitude, report.longitude);
          setLocationStrings(prev => ({
            ...prev,
            [timestamp]: locationString
          }));
        });

      await Promise.all(locationPromises);
    };

    if (breakdownReports.length > 0) {
      loadLocationStrings();
    }
  }, [breakdownReports]);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown time";
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else {
      date = new Date(timestamp);
    }

    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Show PIN auth layer for unauthenticated users
  if (!isAuthenticated && !isVerified && bookingId) {
    return (
      <View style={{ flex: 1, backgroundColor: "#F6F8F7" }}>
        <AuthHeader
          title="Problem Reports"
          subtitle=""
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={{}}
          storeLogo=""
        />
        <PinAuthLayer
          onAuthenticated={() => {
            console.log("✅ PIN verified for ProblemReports:", bookingId);
            setIsVerified(true);
          }}
          bookingId={bookingId}
        />
      </View>
    );
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <AuthHeader
          title="Problem Reports"
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={{}}
        />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#0E7C6B" />
          <Text style={styles.loadingText}>Loading reports...</Text>
        </View>
      </View>
    );
  }

  if (!booking || breakdownReports.length === 0) {
    return (
      <View style={styles.container}>
        <AuthHeader
          title="Problem Reports"
          isDesktop={window.innerWidth >= 768}
          isAuthenticated={isAuthenticated}
          shopInfo={{}}
        />
        <View style={styles.emptyContainer}>
          <Text style={styles.emptyText}>No breakdown reports found</Text>
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.backButton}
            onPress={() => navigate(-1)}
          >
            <Text style={styles.backButtonText}>← Back</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <AuthHeader
        title="Problem Reports"
        isDesktop={window.innerWidth >= 768}
        isAuthenticated={isAuthenticated}
        shopInfo={{}}
      />
      <ScrollView style={styles.scrollView}>
        <View style={styles.content}>
          {/* Back Button at Top */}
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={styles.backButtonTop}
            onPress={() => navigate(-1)}
          >
            <Text style={styles.backButtonText}>← Back to Booking</Text>
          </TouchableOpacity>

          <Text style={styles.title}>
            Breakdown Reports ({breakdownReports.length})
          </Text>
          
          {[...breakdownReports].sort((a, b) => {
            // Incomplete reports first (completed === false or undefined)
            const aCompleted = a.completed === true;
            const bCompleted = b.completed === true;

            if (aCompleted === bCompleted) {
              // If same completion status, sort by timestamp (newest first - latest first)
              const aTime = a.timestamp?.seconds || a.timestamp || 0;
              const bTime = b.timestamp?.seconds || b.timestamp || 0;
              return bTime - aTime; // Descending order (newest/latest first)
            }

            // Incomplete (false) comes before completed (true)
            return aCompleted ? 1 : -1;
          }).map((report, index) => {
            const isUpdating = Object.values(updating).some(v => v === true);
            const reportKey = `${index}_${report.timestamp?.seconds || index}`;
            
            return (
              <View key={reportKey} style={[
                styles.reportCard,
                report.completed === true ? styles.reportCardCompleted : styles.reportCardIncomplete
              ]}>
                {/* Card Title */}
                <Text style={[
                  styles.cardTitle,
                  report.roadsideBreakdown === true ? styles.cardTitleRoadside : styles.cardTitleProblem
                ]}>
                  {report.roadsideBreakdown === true ? "Roadside Breakdown" : "Problem Report"}
                </Text>

                {/* GPS Wrong Warning - Only show if roadside breakdown and location text exists (indicating GPS was wrong) */}
                {report.roadsideBreakdown === true && report.location && report.location.trim() && (
                  <View style={styles.gpsWrongWarning}>
                    <Text style={styles.gpsWrongWarningText}>
                      ⚠️ GPS coordinates were reported as incorrect. Manual location provided.
                    </Text>
                  </View>
                )}

                {/* Media Thumbnails */}
                <View style={styles.mediaThumbnailsContainer}>
                  {report.imageUrls && report.imageUrls.length > 0 && report.imageUrls.map((imageItem, imgIndex) => {
                    // Handle both string URLs and objects with url property
                    const imageUrl = typeof imageItem === 'string' ? imageItem : imageItem.url;
                    if (!imageUrl) return null;

                    return (
                      <TouchableOpacity
                        key={imgIndex}
                        onPress={() => {
                          // Open image in modal for larger viewing
                          setPhotoModalUrl(imageUrl);
                          setIsPhotoModalOpen(true);
                        }}
                        style={styles.thumbnailContainer}
                      >
                        <Image
                          source={{ uri: imageUrl }}
                          style={styles.thumbnailImage}
                          resizeMode="cover"
                        />
                      </TouchableOpacity>
                    );
                  })}
                  {/* Add Photo Button */}
                  {(() => {
                    const reportTimestamp = report.timestamp?.seconds || report.timestamp;
                    const isAddingPhoto = addingPhotoToReport === reportTimestamp;
                    return (
                      <TouchableOpacity dataSet={{ hover: "lift" }}
                        onPress={() => handleAddPhotoToReport(report.timestamp)}
                        style={styles.addPhotoButton}
                        disabled={isAddingPhoto}
                      >
                        {isAddingPhoto ? (
                          <ActivityIndicator size="small" color="#0E7C6B" />
                        ) : (
                          <Text style={styles.addPhotoButtonText}>+</Text>
                        )}
                      </TouchableOpacity>
                    );
                  })()}
                </View>

                {/* Report Details */}
                <View style={styles.reportDetails}>
                  {/* Date */}
                  <Text style={styles.reportTime}>
                    📅 {formatTimestamp(report.timestamp)}
                  </Text>
                  
                  {/* Map Link - Directly below date */}
                  {report.latitude && report.longitude && (() => {
                    const timestamp = report.timestamp?.seconds || report.timestamp;
                    const locationString = locationStrings[timestamp] || "Loading location...";
                    return (
                      <TouchableOpacity
                        onPress={() => handleViewOnMap(report)}
                        style={styles.mapLink}
                      >
                        <Text style={styles.mapLinkText}>
                          📍 {locationString}
                        </Text>
                      </TouchableOpacity>
                    );
                  })()}

                  {/* Description in graphic container */}
                  {report.description && (
                    <View>
                      <Text style={styles.graphicLabel}>Problem Description:</Text>
                      <View style={styles.descriptionGraphic}>
                        <Text style={styles.reportDescription}>
                          {report.description}
                        </Text>
                      </View>
                    </View>
                  )}

                  {/* Location information in graphic container (if location text exists) */}
                  {report.location && report.location.trim() && (
                    <View>
                      <Text style={styles.graphicLabel}>Location:</Text>
                      <View style={styles.locationGraphic}>
                        <Text style={styles.locationText}>
                          {report.location}
                        </Text>
                      </View>
                    </View>
                  )}

                  {/* Completed Toggle */}
                  <View style={styles.toggleContainer}>
                    <Text style={styles.toggleLabel}>
                      {report.completed ? "✅ Completed" : "❌ Not Completed"}
                    </Text>
                    {(() => {
                      const reportTimestamp = report.timestamp?.seconds || report.timestamp;
                      const reportId = `${reportTimestamp}_${Date.now()}`;
                      const isUpdatingThisReport = Object.keys(updating).some(key => key.startsWith(`${reportTimestamp}_`));
                      return isUpdatingThisReport ? (
                        <ActivityIndicator size="small" color="#0E7C6B" />
                      ) : (
                        <Switch
                          value={report.completed === true}
                            onValueChange={() => handleToggleCompleted(report.timestamp, report.completed === true)}
                            trackColor={{ false: "#8A9794", true: "#1F8A5B" }}
                            thumbColor={report.completed ? "#FFFFFF" : "#EDF1F0"}
                          />
                      );
                    })()}
                  </View>
                </View>
              </View>
            );
          })}
        </View>
      </ScrollView>

      {/* Photo Modal */}
      {isPhotoModalOpen && photoModalUrl && (
        <View style={styles.photoModalOverlay}>
          <View style={styles.photoModalContainer}>
            {/* Close Button */}
            <TouchableOpacity dataSet={{ hover: "lift" }}
              style={styles.photoModalCloseButton}
              onPress={() => {
                setIsPhotoModalOpen(false);
                setPhotoModalUrl(null);
              }}
            >
              <Text style={styles.photoModalCloseText}>✕</Text>
            </TouchableOpacity>

            {/* Photo Image */}
            <View style={styles.photoModalImageContainer}>
              {Platform.OS === 'web' ? (
                <img
                  src={photoModalUrl}
                  alt="Photo"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                  }}
                  onError={(error) => {
                    console.error('Error loading photo in modal (web):', error, 'URL:', photoModalUrl);
                  }}
                  onLoad={() => {
                    console.log('Photo loaded in modal (web):', photoModalUrl);
                  }}
                />
              ) : (
                <Image
                  source={{ uri: photoModalUrl }}
                  style={styles.photoModalImage}
                  resizeMode="contain"
                  onError={(error) => {
                    console.error('Error loading photo in modal:', error, 'URL:', photoModalUrl);
                  }}
                  onLoad={(event) => {
                    const { width, height } = event.nativeEvent.source;
                    console.log('Photo loaded in modal:', { width, height, url: photoModalUrl });
                  }}
                />
              )}
            </View>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 20,
    maxWidth: 800,
    alignSelf: "center",
    width: "100%",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: "#5C6B68",
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 40,
  },
  emptyText: {
    fontSize: 18,
    color: "#5C6B68",
    marginBottom: 20,
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 20,
    color: "#2E3A38",
  },
  reportCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    marginBottom: 16,
    padding: 16,
    boxShadow: "0 1px 2px rgba(15, 26, 25, 0.06), 0 2px 8px rgba(15, 26, 25, 0.06)",
    elevation: 3,
    borderLeftWidth: 4,
    borderRightWidth: 4,
  },
  reportCardCompleted: {
    borderLeftColor: "#1F8A5B",
    borderRightColor: "#1F8A5B",
  },
  reportCardIncomplete: {
    borderLeftColor: "#C4453D",
    borderRightColor: "#C4453D",
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 12,
    textAlign: "center",
  },
  cardTitleRoadside: {
    color: "#C4453D",
  },
  cardTitleProblem: {
    color: "#B4761B",
  },
  gpsWrongWarning: {
    backgroundColor: "#FDF3E3",
    borderRadius: 6,
    padding: 12,
    borderWidth: 1,
    borderColor: "#B4761B",
    marginBottom: 12,
  },
  gpsWrongWarningText: {
    fontSize: 14,
    color: "#7A4E12",
    fontWeight: "500",
    lineHeight: 20,
  },
  mediaThumbnailsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 12,
  },
  thumbnailContainer: {
    width: 80,
    height: 80,
    borderRadius: 6,
    overflow: "hidden",
    backgroundColor: "#EDF1F0",
  },
  thumbnailImage: {
    width: "100%",
    height: "100%",
  },
  addPhotoButton: {
    width: 80,
    height: 80,
    borderRadius: 6,
    backgroundColor: "#EDF1F0",
    borderWidth: 2,
    borderColor: "#0E7C6B",
    borderStyle: "dashed",
    justifyContent: "center",
    alignItems: "center",
  },
  addPhotoButtonText: {
    fontSize: 32,
    color: "#0E7C6B",
    fontWeight: "300",
  },
  reportDescription: {
    fontSize: 14,
    color: "#2E3A38",
    marginBottom: 8,
    lineHeight: 20,
  },
  reportDetails: {
    gap: 8,
  },
  reportTime: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 4,
  },
  reportLocation: {
    fontSize: 14,
    color: "#5C6B68",
    marginBottom: 8,
  },
  mapLink: {
    marginTop: 8,
    marginBottom: 12,
  },
  mapLinkText: {
    fontSize: 16,
    color: "#0E7C6B",
    fontWeight: "600",
    textDecorationLine: "none",
  },
  toggleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: "#EDF1F0",
  },
  toggleLabel: {
    fontSize: 16,
    fontWeight: "500",
    color: "#2E3A38",
  },
  graphicLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: "#5C6B68",
    marginBottom: 6,
    marginTop: 4,
  },
  descriptionGraphic: {
    backgroundColor: "#F6F8F7",
    borderRadius: 6,
    padding: 12,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  locationGraphic: {
    backgroundColor: "#E3F2EF",
    borderRadius: 6,
    padding: 12,
    borderWidth: 1,
    borderColor: "#E3F2EF",
  },
  locationText: {
    fontSize: 14,
    color: "#2E3A38",
    lineHeight: 20,
  },
  backButton: {
    backgroundColor: "#0E7C6B",
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
    marginTop: 20,
    marginBottom: 20,
  },
  backButtonTop: {
    backgroundColor: "#0E7C6B",
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
    marginBottom: 20,
  },
  backButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
  photoModalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  photoModalContainer: {
    width: '90%',
    maxWidth: 800,
    backgroundColor: '#0F1A19',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400,
  },
  photoModalCloseButton: {
    position: 'absolute',
    top: 10,
    right: 10,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#FFFFFF',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1001,
  },
  photoModalCloseText: {
    color: '#0F1A19',
    fontSize: 24,
    fontWeight: 'bold',
  },
  photoModalImageContainer: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  photoModalImage: {
    width: '100%',
    height: '100%',
  },
});

export default ProblemReports;

